<!-- Document image annotator — professional viewer/editor dialog. -->
<div class="dia" [class.dia--pan]="panMode">

  <!-- ============ VIEWER HEADER BAR (always visible) ============ -->
  <header class="dia-header" role="toolbar" aria-label="Image viewer">
    <div class="dia-header__left">
      <div class="dia-header__logo"><mat-icon>image</mat-icon></div>
      <div class="dia-header__titlewrap">
        <span class="dia-header__title" [title]="docName">{{ docTitle }}</span>
        <span class="dia-header__badge">{{ fileExtension }}</span>
      </div>
    </div>

    <div class="dia-header__center">
      <div class="dia-header__group">
        <button type="button" class="dia-btn" (click)="zoomOut()" matTooltip="Zoom out" matTooltipPosition="below">
          <mat-icon>zoom_out</mat-icon>
        </button>
        <button type="button" class="dia-btn dia-btn--label" (click)="fitToScreen()" matTooltip="Fit to screen" matTooltipPosition="below">
          {{ zoomPercent }}%
        </button>
        <button type="button" class="dia-btn" (click)="zoomIn()" matTooltip="Zoom in" matTooltipPosition="below">
          <mat-icon>zoom_in</mat-icon>
        </button>
        <button type="button" class="dia-btn" (click)="fitToScreen()" matTooltip="Fit to screen" matTooltipPosition="below">
          <mat-icon>fit_screen</mat-icon>
        </button>
      </div>

      <span class="dia-header__divider"></span>

      <div class="dia-header__group">
        <button type="button" class="dia-btn" (click)="rotateLeft()" matTooltip="Rotate left" matTooltipPosition="below">
          <mat-icon>rotate_left</mat-icon>
        </button>
        <button type="button" class="dia-btn" (click)="rotateRight()" matTooltip="Rotate right" matTooltipPosition="below">
          <mat-icon>rotate_right</mat-icon>
        </button>
      </div>

      <span class="dia-header__divider"></span>

      <div class="dia-header__group">
        <button type="button" class="dia-btn" [class.is-active]="showAnnotations" (click)="toggleAnnotations()"
                matTooltip="Toggle annotations" matTooltipPosition="below">
          <mat-icon>{{ showAnnotations ? 'visibility' : 'visibility_off' }}</mat-icon>
        </button>
        <button type="button" class="dia-btn" [class.is-active]="commentMode" (click)="toggleAddComment()"
                [matTooltip]="commentMode ? 'Click the image to drop a comment' : 'Add comment'" matTooltipPosition="below">
          <mat-icon>{{ commentMode ? 'add_comment' : 'chat_bubble_outline' }}</mat-icon>
        </button>
      </div>
    </div>

    <div class="dia-header__right">
      <!-- Presence: you + anyone else viewing this same image right now -->
      <div class="dia-header__presence" *ngIf="currentUserForHeader">
        <div class="avatar-stack">
          <div class="avatar" [style.background]="currentUserForHeader.color"
               [matTooltip]="currentUserForHeader.name + ' (You)'" matTooltipPosition="below">
            {{ currentUserForHeader.initials }}
            <span class="status-dot status-online"></span>
          </div>
          <div class="avatar" *ngFor="let u of getDisplayedCollaborators(); trackBy: trackByPresence"
               [style.background]="u.color" [matTooltip]="u.name" matTooltipPosition="below">
            {{ u.initials }}
            <span class="status-dot" [ngClass]="'status-' + (u.status || 'online')"></span>
          </div>
          <div class="avatar-more" *ngIf="extraCollaboratorCount > 0"
               [matTooltip]="extraCollaboratorCount + ' more'" matTooltipPosition="below">
            +{{ extraCollaboratorCount }}
          </div>
        </div>
      </div>
      <span class="dia-header__divider"></span>

      <button type="button" class="dia-btn" (click)="toggleFullscreen()"
              [matTooltip]="isFullscreen ? 'Exit full screen' : 'Full screen'" matTooltipPosition="below">
        <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
      </button>
      <button type="button" class="dia-btn" (click)="copyToClipboard()"
              [class.is-active]="copied"
              [matTooltip]="copied ? 'Copied!' : 'Copy image to clipboard'" matTooltipPosition="below">
        <mat-icon>{{ copied ? 'check' : 'content_copy' }}</mat-icon>
      </button>
      <button type="button" class="dia-btn" (click)="export()" matTooltip="Export as PNG" matTooltipPosition="below">
        <mat-icon>download</mat-icon>
      </button>
      <button type="button" class="dia-header__edit" [class.is-active]="isEditMode" (click)="toggleEdit()"
              [matTooltip]="isEditMode ? 'Currently editing' : 'Edit image'" matTooltipPosition="below">
        <mat-icon>edit</mat-icon>
        <span>{{ isEditMode ? 'Editing' : 'Edit' }}</span>
      </button>
      <span class="dia-header__divider"></span>
      <button type="button" class="dia-btn dia-btn--close" (click)="close()" matTooltip="Close (Esc)" matTooltipPosition="below">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <div class="dia__body">
    <!-- Main column: edit toolbar floats above the image stage (rail is a full-height sibling) -->
    <div class="dia__main">

    <!-- ============ EDIT TOOLBAR — floating, draggable overlay (takes no layout height,
         so the image keeps full height / no scroll). Drag by the grip to reposition. ============ -->
    <div class="dia__toolbar-layer" *ngIf="isEditMode">
      <div class="dia__toolbar-float" cdkDrag cdkDragBoundary=".dia__toolbar-layer">
        <button type="button" class="dia__drag-handle" cdkDragHandle
                matTooltip="Drag toolbar" matTooltipPosition="below">
          <span class="dia__grip" aria-hidden="true">
            <i></i><i></i><i></i><i></i><i></i><i></i>
          </span>
        </button>
        <app-doc-annotation-toolbar
          [activeTool]="activeTool" [selectedColor]="selectedColor" [strokeWidth]="strokeWidth"
          [opacity]="opacity" [canUndo]="canUndo" [canRedo]="canRedo" [hasSelection]="!!selectedAnnotationId"
          [showAnnotations]="showAnnotations" [showComments]="showComments" [zoomLevel]="zoom"
          [isFullscreen]="isFullscreen"
          (toolChange)="onTool($event)" (colorChange)="onColor($event)" (strokeChange)="onStroke($event)"
          (opacityChange)="onOpacity($event)" (undo)="undo()" (redo)="redo()" (deleteSelected)="deleteSelectedAnnotation()"
          (toggleAnnotations)="toggleAnnotations()" (toggleComments)="toggleComments()"
          (zoomIn)="zoomIn()" (zoomOut)="zoomOut()" (export)="export()" (toggleFullscreen)="toggleFullscreen()">
        </app-doc-annotation-toolbar>
      </div>
    </div>

    <!-- Stage -->
    <div class="dia__stage" (pointerdown)="onStagePointerDown($event)" (wheel)="onStageWheel($event)">

      <div #stageMedia class="dia__media" [style.transform]="mediaTransform" [style.--dia-zoom]="zoom">
        <!-- No crossorigin on the DISPLAYED image: an S3 response without a matching
             CORS header would make the browser refuse to render it. Export uses its
             own crossOrigin Image() for the canvas, so display and export stay independent. -->
        <img #stageImg class="dia__img" [src]="imageUrl" (load)="onImageLoad()" draggable="false" alt="Document image">

        <app-doc-annotation-canvas
          [annotations]="annotations" [activeTool]="isEditMode ? activeTool : 'select'" [color]="selectedColor"
          [strokeWidth]="strokeWidth" [opacity]="opacity" [selectedId]="selectedAnnotationId"
          [showAnnotations]="showAnnotations"
          (addAnnotation)="onAddAnnotation($event)" (updateAnnotation)="onUpdateAnnotation($event)"
          (selectAnnotation)="onSelectAnnotation($event)" (backgroundClick)="onBackgroundClick($event)"
          (textRequested)="onTextRequested($event)">
        </app-doc-annotation-canvas>

        <!-- Comment pins -->
        <ng-container *ngIf="showComments">
          <button type="button" class="dia__pin" *ngFor="let c of comments; trackBy: trackByComment"
                  [ngClass]="commentPinClass(c.status)" [class.is-active]="activeCommentId === c.id"
                  [style.left.%]="c.x" [style.top.%]="c.y"
                  (click)="selectCommentPin(c.id); $event.stopPropagation()" [title]="c.text">
            <mat-icon>chat_bubble</mat-icon>
          </button>
        </ng-container>

        <!-- New-comment popover -->
        <div class="dia__new-comment" *ngIf="newCommentPos"
             [style.left.%]="newCommentPos.x" [style.top.%]="newCommentPos.y" (click)="$event.stopPropagation()">
          <textarea rows="2" [(ngModel)]="newCommentText" placeholder="Add a comment…"></textarea>
          <div class="dia__new-comment-actions">
            <button type="button" (click)="cancelComment()">Cancel</button>
            <button type="button" class="is-primary" (click)="addComment()">Comment</button>
          </div>
        </div>

        <!-- New-text popover (same styling as the comment popover) -->
        <div class="dia__new-comment" *ngIf="newTextPos"
             [style.left.%]="newTextPos.x" [style.top.%]="newTextPos.y" (click)="$event.stopPropagation()">
          <textarea rows="2" [(ngModel)]="newTextValue" placeholder="Enter text…"
                    (keydown.enter)="$event.preventDefault(); addTextAnnotation()"></textarea>
          <div class="dia__new-comment-actions">
            <button type="button" (click)="cancelText()">Cancel</button>
            <button type="button" class="is-primary" (click)="addTextAnnotation()">Add text</button>
          </div>
        </div>
      </div>

      <div class="dia__export-error" *ngIf="exportError">{{ exportError }}</div>
    </div>
    </div><!-- /.dia__main -->

    <!-- Right rail: annotation properties OR comment sidebar OR document details -->
    <aside class="dia__rail">
      <ng-container *ngIf="selectedAnnotation as ann; else railNoSel">
        <app-doc-annotation-properties [annotation]="ann"
          (update)="onPropUpdate($event)" (deleteAnnotation)="deleteSelectedAnnotation()"
          (close)="onSelectAnnotation(null)">
        </app-doc-annotation-properties>
      </ng-container>
      <ng-template #railNoSel>
        <app-doc-comment-sidebar *ngIf="showCommentSidebar; else details"
          [comments]="comments" [activeCommentId]="activeCommentId"
          (selectComment)="selectCommentPin($event)"
          (addReply)="onCommentReply($event)"
          (updateStatus)="onCommentStatus($event)"
          (updatePriority)="onCommentPriority($event)"
          (deleteComment)="deleteComment($event)"
          (close)="toggleComments()">
        </app-doc-comment-sidebar>
      </ng-template>
      <ng-template #details>
        <div class="dia__meta">
          <div class="dia__meta-title">Document Details</div>
          <div class="dia__meta-row"><span>Name</span><strong>{{ docName }}</strong></div>
          <div class="dia__meta-row"><span>Uploaded by</span><strong>{{ uploadedBy }}</strong></div>
          <div class="dia__meta-row"><span>Date</span><strong>{{ uploadedAt }}</strong></div>
          <div class="dia__meta-row"><span>Size</span><strong>{{ fileSize }}</strong></div>
          <div class="dia__meta-row"><span>Type</span><strong>{{ fileType }}</strong></div>
          <div class="dia__meta-hint">{{ isEditMode ? 'Pick a tool above to annotate, or click the image to drop a comment.' : 'Click Edit to annotate this image.' }}</div>
        </div>
      </ng-template>
    </aside>
  </div>
</div>
